<template>
    <v-container fluid class="mx-0 px-0">
        <div class="stat-tiles-head">
            <span class="stat-tiles-head__bar custom-orange" />
            Στατιστικά
            <v-btn-toggle v-model="option" mandatory class="stats-toggle">
                <v-btn :value="0" small class="stats-toggle__btn">Μέσοι όροι</v-btn>
                <v-btn :value="1" small class="stats-toggle__btn">Σύνολο</v-btn>
            </v-btn-toggle>
        </div>
        <div class="stat-tiles">
            <div v-for="tile in tiles" :key="tile.key" class="stat-tile">
                <v-icon small color="orange">{{ tile.icon }}</v-icon>
                <span class="stat-tile__value">{{ (option ? totalStats : avgStats)[tile.key] }}</span>
                <span class="stat-tile__label">{{ tile.label }}</span>
            </div>
        </div>
        <v-data-table
            :headers="headers"
            :items="[ option ? totalStats : avgStats ]"
            class="stats-table no-global-table-hover elevation-1 rounded-lg no-sticky-first-col"
            hide-default-footer
            disable-sort
            :mobile-breakpoint="0"
            :items-per-page="-1"
        >
            <template v-slot:header.ts_percentage="{ header }">
                <div class="d-inline-flex align-center">
                    <span>{{ header.text }}</span>
                    <info-tip bottom max-width="380" content-class="ts-tooltip-surface">
                        <v-icon
                            small
                            class="ml-1 ts-info-icon"
                            color="orange"
                        >
                            mdi-information-outline
                        </v-icon>
                        <template #content>
                            <div class="ts-tooltip">
                                <div class="font-weight-bold mb-1">True Shooting Percentage (TS%)</div>
                                <div class="mb-2">TS% = Points / (2 x (Field Goal Attempts + 0.44 x Free Throw Attempts))</div>
                                <div class="ts-poor">Below 50%: Poor scoring efficiency</div>
                                <div class="ts-below-average">50% to below 55%: Below average</div>
                                <div class="ts-average">55%: Solid and league average</div>
                                <div class="ts-very-good">57% to 58%: Very good / highly efficient</div>
                                <div class="ts-elite">60% and above: Elite</div>
                            </div>
                        </template>
                    </info-tip>
                </div>
            </template>
            <template v-slot:item.free_throw_percentage="{ item }">
                <v-row dense align="center">
                    <v-col cols="12" align="center">
                        {{ `${item.freeThrowAttempts > 0 ? ((item.freeThrowsMade / item.freeThrowAttempts) * 100).toFixed(1) : 0}% ${option ? `(${item.freeThrowsMade} / ${item.freeThrowAttempts})` : ''}` }}
                    </v-col>
                </v-row>
            </template>
            <template v-slot:item.ts_percentage="{ item }">
                <v-row dense align="center">
                    <v-col cols="12" align="center">
                        <span :class="['font-weight-bold', tsClass(tsPct(item))]">
                            {{ tsPct(item) }}%
                        </span>
                    </v-col>
                </v-row>
            </template>
            <template v-slot:item.two_points_percentage="{ item }">
                <v-row dense align="center">
                    <v-col cols="12" align="center">
                        {{ `${item.twoPointAttempts > 0 ? ((item.twoPointsMade / item.twoPointAttempts) * 100).toFixed(1) : 0}% ${option ? `(${item.twoPointsMade} / ${item.twoPointAttempts})` : ''}` }}
                    </v-col>
                </v-row>
            </template>
            <template v-slot:item.three_points_percentage="{ item }">
                <v-row dense align="center">
                    <v-col cols="12" align="center">
                        {{ `${item.threePointsMade > 0 ? ((item.threePointsMade / item.threePointAttempts) * 100).toFixed(1) : 0}% ${option ? `(${item.threePointsMade} / ${item.threePointAttempts})` : ''}` }}
                    </v-col>
                </v-row>
            </template>
            <template v-slot:item.playTimeInSecs="{ item }">
                <span>
                    {{ secToMinutesSeconds(item.playTimeInSecs) }}
                </span>
            </template>
        </v-data-table>
    </v-container>
</template>

<script>
    export default {
        props: {
            totalStats: {
                type: Object,
                default: null
            },
            avgStats: {
                type: Object,
                default: null
            }
        },
        data() {
            return {
                option: 0,
                tiles: [
                    { key: 'points', label: 'Πόντοι', icon: 'mdi-basketball' },
                    { key: 'rebounds', label: 'Ριμπάουντ', icon: 'mdi-basketball-hoop-outline' },
                    { key: 'assists', label: 'Ασίστ', icon: 'mdi-handshake-outline' },
                    { key: 'steals', label: 'Κλεψίματα', icon: 'mdi-hand-back-left-outline' },
                    { key: 'turnovers', label: 'Λάθη', icon: 'mdi-alert-circle-outline' },
                    { key: 'blockedShots', label: 'Κοψίματα', icon: 'mdi-cancel' }
                ],
                headers: [
                    { text: 'Pts', value: 'points', align: 'center' },
                    { text: '2P%', value: 'two_points_percentage', width: '150px', align: 'center' },
                    { text: '3P%', value: 'three_points_percentage', width: '150px', align: 'center' },
                    { text: 'FT%', value: 'free_throw_percentage', width: '150px', align: 'center' },
                    { text: 'TS%', value: 'ts_percentage', width: '100px', align: 'center' },
                    { text: 'OR', value: 'offensiveRebounds', align: 'center' },
                    { text: 'DR', value: 'defensiveRebounds', align: 'center' },
                    { text: 'TR', value: 'rebounds', align: 'center' },
                    { text: 'AST', value: 'assists', align: 'center' },
                    { text: 'STL', value: 'steals', align: 'center' },
                    { text: 'TO', value: 'turnovers', align: 'center' },
                    { text: 'BLK', value: 'blockedShots', align: 'center' },
                    { text: 'FC', value: 'fouls', align: 'center' },
                    { text: 'PIR', value: 'pir', align: 'center' },
                    { text: 'Min', value: 'playTimeInSecs', align: 'center' }
                ]
            }
        },
        methods: {
            secToMinutesSeconds(ms) {
                if (ms < 0) return '-'
                if (typeof ms !== 'number' || isNaN(ms)) return '-'
                const minutes = Math.floor(ms / 60)
                const seconds = Math.floor(ms % 60)
                if (isNaN(minutes) || isNaN(seconds)) return '-'
                return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`
            },
            tsPct(item = {}) {
                const points = Number(item.points) || 0
                const fga = (Number(item.twoPointAttempts) || 0) + (Number(item.threePointAttempts) || 0)
                const fta = Number(item.freeThrowAttempts) || 0
                const denominator = 2 * (fga + (0.44 * fta))
                if (denominator === 0) return (0).toFixed(1)
                return ((points / denominator) * 100).toFixed(1)
            },
            tsClass(tsValue) {
                const ts = Number(tsValue)
                if (!Number.isFinite(ts)) return 'ts-poor'
                if (ts < 50) return 'ts-poor'
                if (ts < 55) return 'ts-below-average'
                if (ts < 57) return 'ts-average'
                if (ts < 60) return 'ts-very-good'
                return 'ts-elite'
            }
        }
    }
</script>

<style scoped>
/* ---------- Same look as the standings table ---------- */
.stats-table {
    overflow: hidden;
    font-family: Ubuntu, Arial, sans-serif;
}

.stats-table ::v-deep .v-data-table__wrapper table > thead > tr > th {
    background: #0d0d0f !important;
    color: #fff !important;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    white-space: nowrap;
    border-bottom: 3px solid #ff7133 !important;
}

.stats-table ::v-deep .v-data-table__wrapper table > thead > tr > th .v-icon {
    color: #fff !important;
}

.stats-table ::v-deep .v-data-table__wrapper table > tbody > tr > td {
    height: 52px;
    white-space: nowrap;
    background: #fff;
}

.stats-table ::v-deep .v-data-table__wrapper table > tbody > tr:nth-child(even) > td {
    background: #f7f7f8 !important;
}

.stats-table ::v-deep .v-data-table__wrapper table > tbody > tr:hover > td {
    background: #fff3ec !important;
}

.stats-table ::v-deep tbody tr {
    cursor: pointer;
}

/* ---------- Tiles + toggle (same look as the team stats) ---------- */
.stat-tiles-head {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 12px;
    padding: 8px 14px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    font-family: Ubuntu, Arial, sans-serif;
    font-size: 1.1rem;
    font-weight: 800;
}

.stat-tiles-head__bar {
    width: 5px;
    height: 20px;
    border-radius: 2px;
}

.stat-tiles {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 10px;
    margin-bottom: 16px;
    font-family: Ubuntu, Arial, sans-serif;
}

.stat-tile {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    padding: 12px 6px;
    border-radius: 10px;
    background: #fff;
    border-top: 3px solid #ff7133;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.stat-tile__value {
    font-size: 1.6rem;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
}

.stat-tile__label {
    color: rgba(0, 0, 0, 0.55);
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.stats-toggle {
    margin-left: auto;
    background: #0d0d0f !important;
    border-radius: 8px !important;
    padding: 3px;
}

.stats-toggle .stats-toggle__btn {
    background: transparent !important;
    border: none !important;
    border-radius: 6px !important;
    color: rgba(255, 255, 255, 0.75) !important;
    font-weight: 700;
    text-transform: none;
    letter-spacing: normal;
    height: 28px !important;
    font-size: 0.75rem;
}

.stats-toggle .stats-toggle__btn.v-btn--active {
    background: linear-gradient(90deg, #ef4444 0%, #ff7133 50%, orange 100%) !important;
    color: #fff !important;
}

.stats-toggle .stats-toggle__btn.v-btn--active::before {
    opacity: 0 !important;
}

@media (max-width: 960px) {
    .stat-tiles {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
}

@media (max-width: 600px) {
    .stat-tile__value {
        font-size: 1.25rem;
    }

    .stats-toggle {
        margin-left: 0;
    }
}

.ts-info-icon {
    cursor: pointer;
}

.ts-tooltip {
    font-size: 13px;
    line-height: 1.4;
}

::v-deep .v-tooltip__content.ts-tooltip-surface {
    background: #fff !important;
    opacity: 1 !important;
    color: #111;
    border: 1px solid #d7dbe3;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

.ts-poor {
    color: #d32f2f;
}

.ts-below-average {
    color: #ef6c00;
}

.ts-average {
    color: #f9a825;
}

.ts-very-good {
    color: #2e7d32;
}

.ts-elite {
    color: #1b5e20;
}

::v-deep .no-sticky-first-col .v-data-table__wrapper table > tbody > tr > td:nth-child(1),
::v-deep .no-sticky-first-col .v-data-table__wrapper table > thead > tr > th:nth-child(1) {
    position: static !important;
    left: auto !important;
    z-index: auto !important;
}

.stats-table ::v-deep .v-data-table__wrapper table > tbody > tr:hover > td:first-child {
    box-shadow: inset 4px 0 0 #ff7133;
}
</style>
